import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, AlertTriangle, Ban, Mail, Users, Scale, FileText, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import ContactFormModal from '../components/contact/ContactFormModal';

export default function TermsPage() {
  const navigate = useNavigate();
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const lastUpdated = "January 28, 2026";

  return (
    <div className="min-h-screen bg-[#0a0a18] text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#0a0a18]/90 backdrop-blur-lg border-b border-[#2d2d44]/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/mpm-logo.png" alt="MyPatriotMail" className="w-10 h-10 object-contain" />
            <span className="text-lg font-bold">MyPatriotMail</span>
          </div>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#6D4AFF]/20 rounded-2xl mb-6">
            <Scale className="w-8 h-8 text-[#6D4AFF]" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-black mb-4" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            Terms of Service
          </h1>
          <p className="text-gray-400">
            Last Updated: {lastUpdated}
          </p>
        </div>

        {/* Important Notice Banner */}
        <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-xl p-6 mb-10">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-red-400 mb-2">Zero Tolerance Policy</h2>
              <p className="text-gray-300">
                MyPatriotMail has a <strong className="text-white">strict zero-tolerance policy</strong> against spam, 
                mass unsolicited emails, and any form of email abuse. Violations will result in 
                <strong className="text-red-400"> immediate account termination</strong> without refund.
              </p>
            </div>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="bg-[#1a1a2e] border border-[#2d2d44] rounded-xl p-6 mb-10">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#6D4AFF]" />
            Table of Contents
          </h3>
          <nav className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            {[
              { num: '1', title: 'Acceptance of Terms' },
              { num: '2', title: 'Service Description' },
              { num: '3', title: 'Acceptable Use Policy' },
              { num: '4', title: 'Prohibited Activities' },
              { num: '5', title: 'Anti-Spam Policy' },
              { num: '6', title: 'Referral Program' },
              { num: '7', title: 'Account Responsibilities' },
              { num: '8', title: 'Privacy & Encryption' },
              { num: '9', title: 'Termination' },
              { num: '10', title: 'Limitation of Liability' },
              { num: '11', title: 'Data Delivery & Retention' },
              { num: '12', title: 'Contact Information' }
            ].map((item) => (
              <a 
                key={item.num}
                href={`#section-${item.num}`}
                className="text-gray-400 hover:text-[#6D4AFF] transition-colors py-1"
              >
                {item.num}. {item.title}
              </a>
            ))}
          </nav>
        </div>

        {/* Terms Sections */}
        <div className="space-y-10">
          {/* Section 1 */}
          <section id="section-1" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#6D4AFF] rounded-lg flex items-center justify-center text-sm">1</span>
              Acceptance of Terms
            </h2>
            <div className="text-gray-300 space-y-4 pl-11">
              <p>
                By accessing, registering for, or using MyPatriotMail ("Service"), you agree to be bound by these 
                Terms of Service ("Terms"). If you do not agree to these Terms, you may not use the Service.
              </p>
              <p>
                These Terms constitute a legally binding agreement between you ("User") and MyPatriotMail ("we," "us," or "our"). 
                We reserve the right to modify these Terms at any time, and such modifications will be effective immediately 
                upon posting. Your continued use of the Service constitutes acceptance of any modified Terms.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section id="section-2" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#6D4AFF] rounded-lg flex items-center justify-center text-sm">2</span>
              Service Description
            </h2>
            <div className="text-gray-300 space-y-4 pl-11">
              <p>
                MyPatriotMail is a secure, encrypted email platform designed for private, person-to-person communication 
                within our closed network. The Service is intended for:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Personal correspondence between individuals</li>
                <li>Professional business communications</li>
                <li>Private, secure messaging within the @mypatriotmail.org network</li>
                <li>Normal, everyday email usage patterns</li>
              </ul>
              <p>
                The Service is <strong className="text-white">NOT</strong> intended for mass communication, marketing campaigns, 
                bulk messaging, or any form of automated email distribution.
              </p>
            </div>
          </section>

          {/* Section 3 - Acceptable Use */}
          <section id="section-3" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#6D4AFF] rounded-lg flex items-center justify-center text-sm">3</span>
              Acceptable Use Policy
            </h2>
            <div className="text-gray-300 space-y-4 pl-11">
              <p className="font-semibold text-white">You agree to use MyPatriotMail only for lawful purposes and normal email communication. Acceptable uses include:</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="font-bold text-green-400">Permitted Uses</span>
                  </div>
                  <ul className="text-sm space-y-2 text-gray-300">
                    <li>✓ Personal emails to friends and family</li>
                    <li>✓ Business correspondence with clients</li>
                    <li>✓ Professional networking</li>
                    <li>✓ Account notifications you subscribe to</li>
                    <li>✓ Sharing files with known recipients</li>
                    <li>✓ Group discussions with consent</li>
                  </ul>
                </div>
                
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <XCircle className="w-5 h-5 text-red-500" />
                    <span className="font-bold text-red-400">Prohibited Uses</span>
                  </div>
                  <ul className="text-sm space-y-2 text-gray-300">
                    <li>✗ Mass marketing emails</li>
                    <li>✗ Unsolicited bulk messages</li>
                    <li>✗ Automated email campaigns</li>
                    <li>✗ Purchased mailing lists</li>
                    <li>✗ Spam of any kind</li>
                    <li>✗ Phishing or scam attempts</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4 - Prohibited Activities */}
          <section id="section-4" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#6D4AFF] rounded-lg flex items-center justify-center text-sm">4</span>
              Prohibited Activities
            </h2>
            <div className="text-gray-300 space-y-4 pl-11">
              <p>The following activities are strictly prohibited and will result in immediate account termination:</p>
              
              <div className="bg-[#1a1a2e] border border-[#2d2d44] rounded-xl p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <Ban className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-bold text-white">Spam & Bulk Email</span>
                    <p className="text-sm text-gray-400">Sending unsolicited messages to multiple recipients, regardless of content</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Ban className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-bold text-white">Email Harvesting</span>
                    <p className="text-sm text-gray-400">Collecting email addresses from our platform for any purpose</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Ban className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-bold text-white">Malware Distribution</span>
                    <p className="text-sm text-gray-400">Sending viruses, trojans, or any malicious software</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Ban className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-bold text-white">Phishing & Fraud</span>
                    <p className="text-sm text-gray-400">Attempting to deceive users or obtain sensitive information</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Ban className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-bold text-white">Illegal Content</span>
                    <p className="text-sm text-gray-400">Transmitting content that violates any applicable laws</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Ban className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-bold text-white">Harassment & Threats</span>
                    <p className="text-sm text-gray-400">Using the Service to harass, threaten, or intimidate others</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Ban className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-bold text-white">Automated Systems</span>
                    <p className="text-sm text-gray-400">Using bots, scripts, or automated tools to send emails</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5 - Anti-Spam Policy (HIGHLIGHTED) */}
          <section id="section-5" className="scroll-mt-24">
            <div className="bg-gradient-to-r from-[#6D4AFF]/20 to-[#9D7AFF]/20 border-2 border-[#6D4AFF] rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-[#6D4AFF] rounded-xl flex items-center justify-center">
                  <Mail className="w-5 h-5" />
                </span>
                Anti-Spam Policy
              </h2>
              
              <div className="space-y-6 text-gray-300">
                <div className="bg-[#0a0a18]/50 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-white mb-3">Definition of Spam</h3>
                  <p>
                    For the purposes of this Agreement, "spam" is defined as any unsolicited bulk email (UBE), 
                    unsolicited commercial email (UCE), or any message sent to recipients who have not explicitly 
                    consented to receive such communications from the sender.
                  </p>
                </div>

                <div className="bg-[#0a0a18]/50 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-white mb-3">Email Volume Limits</h3>
                  <p className="mb-4">To ensure fair use and prevent abuse, the following limits apply:</p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-[#6D4AFF] rounded-full"></span>
                      Email limits are determined by your subscription plan
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-[#6D4AFF] rounded-full"></span>
                      Unusual sending patterns may trigger security reviews
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-[#6D4AFF] rounded-full"></span>
                      Rapid sending to multiple recipients is monitored
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-[#6D4AFF] rounded-full"></span>
                      All emails must be to recipients who expect your correspondence
                    </li>
                  </ul>
                </div>

                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-red-400 mb-3">Consequences of Spam Violations</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="text-red-500 font-bold">1st Offense:</span>
                      <span>Immediate account suspension pending investigation</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-500 font-bold">Confirmed Violation:</span>
                      <span>Permanent account termination without refund</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-500 font-bold">Severe Cases:</span>
                      <span>Legal action and reporting to relevant authorities</span>
                    </li>
                  </ul>
                </div>

                <p className="text-sm text-gray-400 italic">
                  We reserve the right to determine, in our sole discretion, whether any email activity constitutes spam. 
                  Our decision is final and not subject to appeal.
                </p>
              </div>
            </div>
          </section>

          {/* Section 6 - Referral Program */}
          <section id="section-6" className="scroll-mt-24">
            <div className="bg-gradient-to-r from-amber-500/10 to-purple-500/10 border-2 border-amber-500/50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-gradient-to-r from-amber-500 to-purple-500 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </span>
                Referral Program
              </h2>
              
              <div className="space-y-6 text-gray-300">
                <p>
                  MyPatriotMail offers a referral program to reward users who help grow our community of privacy-conscious patriots. 
                  By participating, you can earn free subscription time.
                </p>

                <div className="bg-[#0a0a18]/50 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-white mb-4">Reward Tiers</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4 p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
                      <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl">🏆</span>
                      </div>
                      <div>
                        <p className="font-bold text-amber-400">10 Paid Referrals = 1 Year Free</p>
                        <p className="text-sm text-gray-400 mt-1">
                          For every 10 people you refer who become paid subscribers, you receive one free year of "The Patriot" plan.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-500/30">
                      <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl">👑</span>
                      </div>
                      <div>
                        <p className="font-bold text-transparent bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text">
                          50 Paid Referrals = FREE FOR LIFE
                        </p>
                        <p className="text-sm text-gray-400 mt-1">
                          Achieve 50 paid referrals and receive "The Patriot" plan <strong className="text-purple-400">absolutely free, forever</strong>. 
                          This is our ultimate reward for our most dedicated advocates.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-[#0a0a18]/50 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-white mb-3">Program Rules</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="w-2 h-2 bg-[#6D4AFF] rounded-full mt-2"></span>
                      <span>You may send up to <strong className="text-white">10 invitations per week</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-2 h-2 bg-[#6D4AFF] rounded-full mt-2"></span>
                      <span>Referrals must be genuine individuals who sign up and maintain a paid subscription</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-2 h-2 bg-[#6D4AFF] rounded-full mt-2"></span>
                      <span>Self-referrals, fake accounts, or fraudulent referrals will result in disqualification</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-2 h-2 bg-[#6D4AFF] rounded-full mt-2"></span>
                      <span>Rewards are applied automatically when milestones are reached</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-2 h-2 bg-[#6D4AFF] rounded-full mt-2"></span>
                      <span>MyPatriotMail reserves the right to modify or terminate the referral program at any time</span>
                    </li>
                  </ul>
                </div>

                <p className="text-sm text-gray-400 italic">
                  Abuse of the referral program, including creating fake accounts or engaging in referral fraud, 
                  will result in immediate account termination and forfeiture of all rewards.
                </p>
              </div>
            </div>
          </section>

          {/* Section 7 */}
          <section id="section-7" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#6D4AFF] rounded-lg flex items-center justify-center text-sm">7</span>
              Account Responsibilities
            </h2>
            <div className="text-gray-300 space-y-4 pl-11">
              <p>As a MyPatriotMail user, you are responsible for:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Ensuring your use complies with these Terms and applicable laws</li>
                <li>Promptly notifying us of any unauthorized account access</li>
                <li>Keeping your account information accurate and up-to-date</li>
              </ul>
              <p>
                You may not share your account with others or allow multiple people to use a single account. 
                Each user must have their own individual account.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section id="section-8" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#6D4AFF] rounded-lg flex items-center justify-center text-sm">8</span>
              Privacy & Encryption
            </h2>
            <div className="text-gray-300 space-y-4 pl-11">
              <p>
                MyPatriotMail employs AES-256 encryption to protect your communications. Our privacy practices include:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-white">Zero-Knowledge Architecture:</strong> We cannot read the contents of your encrypted emails</li>
                <li><strong className="text-white">No Third-Party Sharing:</strong> We do not sell or share your data with advertisers</li>
                <li><strong className="text-white">Minimal Data Collection:</strong> We only collect data necessary to provide the Service</li>
                <li><strong className="text-white">Secure Infrastructure:</strong> All data is stored on US-based servers with enterprise security</li>
              </ul>
              <p>
                While we protect your privacy to the fullest extent, we may be required to comply with valid legal 
                requests from law enforcement agencies with proper jurisdiction and documentation.
              </p>
            </div>
          </section>

          {/* Section 9 */}
          <section id="section-9" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#6D4AFF] rounded-lg flex items-center justify-center text-sm">9</span>
              Termination
            </h2>
            <div className="text-gray-300 space-y-4 pl-11">
              <p>We reserve the right to terminate or suspend your account at any time for:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Violation of these Terms of Service</li>
                <li>Engaging in spam or prohibited activities</li>
                <li>Fraudulent or illegal activity</li>
                <li>Non-payment of subscription fees</li>
                <li>Any reason we deem necessary to protect the Service or other users</li>
              </ul>
              <p>
                Upon termination for Terms violations, you forfeit any remaining subscription time and are not 
                entitled to a refund. You may also be prohibited from creating new accounts.
              </p>
            </div>
          </section>

          {/* Section 10 */}
          <section id="section-10" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#6D4AFF] rounded-lg flex items-center justify-center text-sm">10</span>
              Limitation of Liability
            </h2>
            <div className="text-gray-300 space-y-4 pl-11">
              <p>
                THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND. TO THE MAXIMUM EXTENT PERMITTED BY LAW, 
                MYPATRIOTMAIL DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED 
                WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
              <p>
                IN NO EVENT SHALL MYPATRIOTMAIL BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, 
                OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR USE, ARISING OUT OF 
                OR IN CONNECTION WITH THE SERVICE.
              </p>
            </div>
          </section>

          {/* Section 11 - Data Delivery & Retention */}
          <section id="section-11" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#6D4AFF] rounded-lg flex items-center justify-center text-sm">11</span>
              Data Delivery & Retention
            </h2>
            <div className="text-gray-300 space-y-4 pl-11">
              <p>
                MyPatriotMail provides a <strong className="text-white">secure encrypted channel</strong> for transmitting 
                information between users within the @mypatriotmail.org network. All messages are encrypted using AES-256 
                encryption and transmitted over secure connections.
              </p>
              <p>
                <strong className="text-white">No Guarantee Against Data Loss.</strong> While we employ industry-standard 
                encryption and security practices, MyPatriotMail is not responsible for any loss of data due to server 
                outages, power failures, hardware malfunctions, failed backups, natural disasters, cyberattacks, or any 
                other circumstances beyond our reasonable control. We make no guarantees regarding the perpetual 
                availability or preservation of any data stored on the platform.
              </p>
              <p>
                <strong className="text-white">User Responsibility.</strong> It is strongly recommended that once you 
                receive secure data — including emails, attachments, media files, or any other content — you 
                <strong className="text-[#6D4AFF]"> download it to your personal device or delete it immediately</strong> if 
                it is no longer needed. Users are solely responsible for maintaining their own backups of critical 
                information. MyPatriotMail should not be treated as a permanent or archival storage solution.
              </p>
              <p>
                <strong className="text-white">Service "As-Is" Delivery.</strong> The secure transmission service is 
                provided on an "as-is" and "as-available" basis. We do not warrant that the service will be uninterrupted, 
                error-free, or that any data will be retained indefinitely. By using the platform, you acknowledge and 
                accept these limitations.
              </p>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mt-4">
                <p className="text-amber-300 text-sm font-medium mb-1">Important Notice</p>
                <p className="text-sm text-amber-200/70">
                  MyPatriotMail serves as a secure delivery mechanism for your communications. We are not liable for 
                  data loss under any circumstances. Please download or export important information promptly and 
                  maintain your own records.
                </p>
              </div>
            </div>
          </section>

          {/* Section 12 */}
          <section id="section-12" className="scroll-mt-24">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#6D4AFF] rounded-lg flex items-center justify-center text-sm">12</span>
              Contact Information
            </h2>
            <div className="text-gray-300 space-y-4 pl-11">
              <p>
                If you have questions about these Terms of Service or need to report a violation, please contact us:
              </p>
              <div className="bg-[#1a1a2e] border border-[#2d2d44] rounded-xl p-6 text-center">
                <Mail className="w-10 h-10 text-[#6D4AFF] mx-auto mb-4" />
                <p className="text-gray-400 mb-4">Use our secure contact form for support, abuse reports, or legal inquiries.</p>
                <Button
                  onClick={() => setContactModalOpen(true)}
                  className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
                  data-testid="terms-contact-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Contact Us
                </Button>
              </div>
            </div>
          </section>
        </div>

        {/* Agreement Section */}
        <div className="mt-16 p-8 bg-gradient-to-r from-[#1a1a2e] to-[#252540] rounded-2xl border border-[#2d2d44] text-center">
          <Shield className="w-12 h-12 text-[#6D4AFF] mx-auto mb-4" />
          <h3 className="text-xl font-bold mb-3">By Using MyPatriotMail</h3>
          <p className="text-gray-400 max-w-2xl mx-auto mb-6">
            You acknowledge that you have read, understood, and agree to be bound by these Terms of Service. 
            You also agree to use the Service responsibly and in accordance with all applicable laws.
          </p>
          <Button 
            onClick={() => navigate('/register')}
            className="bg-[#6D4AFF] hover:bg-[#5B3FD9] px-8"
          >
            Create Your Account
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#2d2d44]/50 py-8 px-4 mt-16">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-500">
          <p>© 2026 MyPatriotMail. All rights reserved.</p>
          <div className="flex gap-6">
            <button onClick={() => navigate('/privacy')} className="hover:text-[#6D4AFF] transition-colors">Privacy Policy</button>
            <button onClick={() => navigate('/terms')} className="text-[#6D4AFF]">Terms of Service</button>
            <button onClick={() => navigate('/roadmap')} className="hover:text-[#6D4AFF] transition-colors">Roadmap</button>
            <button onClick={() => navigate('/')} className="hover:text-[#6D4AFF] transition-colors">Home</button>
          </div>
        </div>
      </footer>
      
      {/* Contact Form Modal */}
      <ContactFormModal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
      />
    </div>
  );
}
